import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  AgxRow,
  AgxColumn,
  AgxDatePicker,
} from '@urbanx/agx-ui-components';
import { AuctionDetails } from './types';
import './AuctionDetails.scss';
import { AgxTextInput } from '@urbanx/agx.ui-components2';
import {
  getLocalAuctionDateTime,
  getUtcAuctionDateTime,
} from './auctionDateTime';

interface AuctionDetailsProps {
  id: string;
  validate?: boolean;
  required?: boolean;
  onValueChanged: (value: { id: string; value: AuctionDetails }) => void;
  defaultValue: AuctionDetails;
}

const AuctionDetailsComponent = ({
  id,
  validate,
  required,
  onValueChanged,
  defaultValue,
}: AuctionDetailsProps) => {
  const [auctionDetails, setAuctionDetails] = useState({
    auctionDate: getLocalAuctionDateTime(defaultValue?.auctionDate),
    location: defaultValue?.location ?? '',
  });

  const { auctionDate, location } = auctionDetails;
  const [auctionTime, setAuctionTime] = useState(
    dayjs(auctionDate).format('HH:mm')
  );

  useEffect(() => {
    onValueChanged({
      id,
      value: {
        ...auctionDetails,
        auctionDate: getUtcAuctionDateTime(auctionDate),
      },
    });
    setAuctionTime(auctionDate.format('HH:mm'));
  }, [auctionDetails]);

  return (
    <AgxColumn largeGap fill key={id}>
      <AgxRow extraClasses="startAlign" largeGap fill>
        <AgxDatePicker
          id={`${id}-date`}
          key={`${id}-date`}
          label="Auction Date"
          required
          defaultValue={auctionDate.format('YYYY-MM-DD')}
          onValueChanged={({ value }) => {
            // Parse the date string from the picker (e.g., "2026-02-15")
            const datePortion = dayjs(value, 'YYYY-MM-DD');

            // This avoids string parsing which can fail in Safari
            // Example: If auctionDate is "2026-02-12 14:30" and user selects "2026-02-15"
            // Result: "2026-02-15 14:30"
            const constructedDate = datePortion
              .hour(auctionDate.hour())
              .minute(auctionDate.minute());

            if (constructedDate.isValid()) {
              setAuctionDetails(prev => ({
                ...prev,
                auctionDate: constructedDate,
              }));
            }
          }}
          date
          stretch
          error={
            validate && !defaultValue?.auctionDate ? 'Enter a date' : undefined
          }
        />
        <AgxDatePicker
          id={`${id}-time`}
          key={`${id}-time`}
          label="Auction Time"
          required={required}
          blankValue={auctionTime !== '00:00' ? false : true}
          defaultValue={auctionTime}
          onValueChanged={({ value }) => {
            // Parse the time string manually (e.g., "14:30" -> ["14", "30"])
            // This avoids string-based date parsing which fails in Safari
            const timeParts = (value || '00:00').split(':');
            const hours = parseInt(timeParts[0], 10);
            const minutes = parseInt(timeParts[1], 10);

            const constructedDate = auctionDate
              .hour(hours)
              .minute(minutes);

            if (constructedDate.isValid()) {
              setAuctionTime(constructedDate.format('HH:mm'));
              setAuctionDetails(prev => ({
                ...prev,
                auctionDate: constructedDate,
              }));
            }
          }}
          date={false}
          stretch
        />
      </AgxRow>
      <AgxTextInput
        id={'auctionDetailsLocation'}
        label="Location"
        type='text'
        required={required}
        onInputValueChange={({ value }) => {
          setAuctionDetails(prev => ({
            ...prev,
            location: value,
          }));
        }}
        stretch
        defaultValue={location}
      ></AgxTextInput>
    </AgxColumn>
  );
};

export default AuctionDetailsComponent;
